import { CORE_API_URL, LOGIN_URL } from '../config';

export const authService = {
  async login(email, password) {
    // Every method in this file now goes to Go, and to ONE Go service: login,
    // logout, getCurrentUser and changePassword used to address cmd/auth via a
    // separate AUTH_API_URL, but cmd/auth folded into cmd/api, so they share
    // CORE_API_URL with the setup wizard, the organisation settings and the
    // manager's team list. Nothing here reaches the Python api.
    //
    // A9 removed the six methods that were still on API_BASE_URL — pipeline
    // runs, intelligence runs, intelligence settings and scoring config —
    // together with the Admin Console tabs that called them. Re-adding an
    // API_BASE_URL fetch here would point a live screen at a service that is
    // not deployed outside production, so it fails as localhost in dev and as
    // a retired schema in production. See
    // docs/superpowers/specs/2026-08-02-a9-decommission-python-design.md.
    const response = await fetch(`${CORE_API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const data = await response.json();
    sessionStorage.setItem('signalcue_token', data.access_token);
    sessionStorage.setItem('signalcue_user', JSON.stringify(data.user));
    return data;
  },

  async logout() {
    const token = sessionStorage.getItem('signalcue_token');
    if (token) {
      try {
        await fetch(`${CORE_API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    sessionStorage.clear();
  },

  // handleSessionExpired clears the (now-invalid) session and hard-redirects to the
  // login page, so an expired session never leaves the app in a broken half-state —
  // a frozen job panel, or a "not signed in" banner over an empty page. Guarded so
  // many concurrent 401s trigger a single redirect.
  //
  // Reached two ways, and for a long time only one of them worked. A 401
  // RESPONSE is handled by whichever client checks for it. A MISSING token was
  // not: every method here and in seven services checked for one itself and
  // threw a plain Error, short-circuiting before any request — so no 401 came
  // back and this never ran. That is now routed through requireToken() below;
  // see its comment for the failure it produced. Take a token from
  // requireToken(), never from sessionStorage directly, or the gap reopens.
  _redirectingToLogin: false,
  handleSessionExpired() {
    if (this._redirectingToLogin) return;
    this._redirectingToLogin = true;
    try {
      sessionStorage.clear();
    } catch (_) {
      /* storage unavailable — still redirect */
    }
    // Mirror ProtectedRoute's login target: LOGIN_URL (config.js) -- the
    // configured login host, or (unconfigured) the current origin.
    window.location.assign(`${LOGIN_URL}/login`);
  },

  async changePassword(currentPassword, newPassword) {
    const token = sessionStorage.getItem('signalcue_token');
    const response = await fetch(`${CORE_API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password change failed');
    }
    
    return response.json();
  },

  async getCurrentUser() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) return null;

    const response = await fetch(`${CORE_API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      sessionStorage.clear();
      return null;
    }

    return response.json();
  },

  getToken() {
    return sessionStorage.getItem('signalcue_token');
  },

  // requireToken is the ONE place a missing session turns into a redirect, and
  // every API client should get its token through it.
  //
  // A missing token and a rejected token are the same event to a user: the
  // session is over. But they took different paths. Each service checked for a
  // token itself and threw a plain Error, which short-circuited BEFORE any
  // request — so no 401 ever came back, so the 401 branch that DOES redirect
  // never ran. The result was a page rendering "Not authenticated" over stale
  // numbers, with a header reading "User / User" and no way forward. Seven
  // services had that shape; reported from the Data page on 2026-08-02.
  //
  // Throwing as well as redirecting is deliberate: the caller must still abort,
  // because window.location.assign does not stop the current tick.
  requireToken() {
    const token = this.getToken();
    if (!token) {
      this.handleSessionExpired();
      throw new Error('Not authenticated');
    }
    return token;
  },

  // getUser never throws. That is a hard requirement, not defensiveness: this now
  // runs in App.jsx's RENDER path on every render, plus ProtectedRoute and
  // NotFound, so a throw here is a white screen on every route in the tab —
  // including /login, which is the one page that could get you out of it. The
  // only recovery would be clearing storage by hand.
  //
  // And the bad value is a real path, not a hypothetical. login() does
  // sessionStorage.setItem('signalcue_user', JSON.stringify(data.user)); if a
  // login response carries no `user` key, JSON.stringify(undefined) is the
  // JS value undefined, setItem coerces it to the literal STRING "undefined",
  // and JSON.parse("undefined") throws. AuthHandler and ProtectedRoute also
  // write this key straight from a URL parameter, which is attacker-supplied.
  // A session we cannot read is a session we do not have: return null and let
  // the caller's signed-out path run.
  getUser() {
    const userStr = sessionStorage.getItem('signalcue_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch (_) {
      return null;
    }
  },

  isAuthenticated() {
    return !!this.getToken();
  },

  // Organisation management methods (Step 3). A6 moved all three to the Go core
  // API (CORE_API_URL); A9 removed the Admin Console's last Python callers.
  async getOrganisation() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/organisation`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Organisation not found - return null instead of throwing
        return null;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get organisation details');
    }

    return response.json();
  },

  async updateOrganisation(updates) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/organisation`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update organisation');
    }

    return response.json();
  },

  async getOrganisationStatus() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/organisation/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Return default status for new organisations
        return {
          setup_status: 'not_configured',
          progress_percentage: 0,
          completed_steps: [],
          pending_steps: [
            'Configure organisation details',
            'Add team members',
            'Configure territories and sectors',
            'Define recruiter specialisations'
          ]
        };
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get organisation status');
    }

    return response.json();
  },

  // Setup Wizard methods. Task 7 (A3) moved these ~18 endpoints off the Python
  // api onto cmd/api, the Go core API (CORE_API_URL). The Python copies still
  // exist in api/main.py because that directory remains production's build
  // source (A9), but nothing in the product calls them.
  async getSetupWizardState() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/state`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get wizard state');
    }

    return response.json();
  },

  async addTeamMember(memberData) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/team-members`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(memberData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add team member');
    }

    return response.json();
  },

  async deleteTeamMember(userId) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/team-members/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete team member');
    }

    return response.json();
  },

  async getTeamMembers() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/team-members`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get team members');
    }

    return response.json();
  },

  async assignManager(recruiterUserId, managerUserId) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/assign-manager`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        recruiter_user_id: recruiterUserId,
        manager_user_id: managerUserId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to assign manager');
    }

    return response.json();
  },

  async getManagers() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/managers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get managers');
    }

    return response.json();
  },

  async getRecruiters() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/recruiters`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get recruiters');
    }

    return response.json();
  },

  async getRecruitersAndManagers() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/recruiters-and-managers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get recruiters and managers');
    }

    return response.json();
  },

  async getTerritories() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/territories`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get territories');
    }

    return response.json();
  },

  async createTerritory(name) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/territories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create territory');
    }

    return response.json();
  },

  async getSectors() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/sectors`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get sectors');
    }

    return response.json();
  },

  async createSector(name) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/sectors`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create sector');
    }

    return response.json();
  },

  // The catalogue's vocabulary (Task 6): the industries, functions and
  // countries a territory/sector label's text must match to bind to real
  // data. The setup wizard and Manage Team's scope pickers read this to
  // steer an admin onto text that works instead of a typo ("SG") or an
  // off-vocabulary word ("Fintech") that matches nothing.
  async getScopeVocabulary() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/vocabulary`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get the scope vocabulary');
    }

    return response.json();
  },

  async getRoleFamilies() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/role-families`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get role families');
    }

    return response.json();
  },

  async createRoleFamily(name) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/role-families`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create role family');
    }

    return response.json();
  },

  async defineRecruiterSpecialisation(recruiterUserId, specialisationData) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/recruiter-specialisation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        recruiter_user_id: recruiterUserId,
        ...specialisationData
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to define specialisation');
    }

    return response.json();
  },

  async getRecruiterSpecialisation(recruiterUserId) {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/recruiter-specialisation/${recruiterUserId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get specialisation');
    }

    return response.json();
  },

  async getSetupReview() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/review`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get setup review');
    }

    return response.json();
  },

  async completeSetup() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/admin/setup-wizard/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ confirm: true })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to complete setup');
    }

    return response.json();
  },

  // The Manage Team page's member list. It moved to the Go core API because it
  // had to, not for tidiness: the Python copy reads core.recruiter_manager,
  // which migration 000031 dropped, so on a Go-migrated database this page
  // loaded its dropdowns (already on CORE_API_URL, A3) and then 500'd on the
  // list of people it exists to edit. Same class as validateInvitationToken.
  async getManagedTeamMembers() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/manager/team-members`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to load managed team members');
    }

    return response.json();
  },

  // Password setup with invitation token. Both of these moved to the Go core API
  // (CORE_API_URL) because they had to: migration 000031 dropped
  // auth.user_invitation_token, which the Python copies still read, so calling
  // them 500s on every invitation. cmd/api reads auth.user_verify_token instead.
  // They take no bearer token — the invitation token IS the credential.
  async validateInvitationToken(token) {
    const response = await fetch(`${CORE_API_URL}/api/public/setup-password/validate?token=${encodeURIComponent(token)}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid invitation token');
    }

    return response.json();
  },

  // The recruiter's desk (Task 5): territories/sectors that resolve into a
  // server-side filter for Candidate Graph and Targeted Outreach. Read by
  // ScopeFilterBar, which decides on screen whether a label is narrowing
  // anything at all -- match_key never renders, only `name` does.
  async getMyScope() {
    const token = this.requireToken();

    const response = await fetch(`${CORE_API_URL}/api/me/scope`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to read your desk');
    }

    return response.json();
  },

  async setupPassword(token, password) {
    const response = await fetch(`${CORE_API_URL}/api/public/setup-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to set password');
    }

    return response.json();
  }
};